import * as React from 'react';
import type { Editor } from '@tiptap/react';

import { OpenHeaderFooter, openHeaderFooterKey, readOpenHeaderFooter } from 'containers/Tiptap/lib/header-footer-zone';

/**
 * The header or footer Pages has open, read from its (non-observable) storage. Holds only a derived
 * key in state, so collaborative transactions on the body re-render nothing until a zone opens,
 * closes or switches.
 */
export function useOpenHeaderFooter(bodyEditor: Editor | null): OpenHeaderFooter | null {
  const [key, setKey] = React.useState<string | null>(() => openHeaderFooterKey(readOpenHeaderFooter(bodyEditor)));

  React.useEffect(() => {
    const sync = (): void => setKey(openHeaderFooterKey(readOpenHeaderFooter(bodyEditor)));
    sync();
    if (!bodyEditor) return undefined;
    bodyEditor.on('transaction', sync);
    bodyEditor.on('update', sync);
    return () => {
      bodyEditor.off('transaction', sync);
      bodyEditor.off('update', sync);
    };
  }, [bodyEditor]);

  return key ? readOpenHeaderFooter(bodyEditor) : null;
}

/**
 * Returns the open header/footer overlay editor (or `null`).
 */
export function useHeaderFooterOverlayEditor(bodyEditor: Editor | null): Editor | null {
  const overlayEditor = useOpenHeaderFooter(bodyEditor)?.editor ?? null;

  const [, forceRender] = React.useReducer((n: number): number => n + 1, 0);
  React.useEffect(() => {
    if (!overlayEditor) return undefined;
    overlayEditor.on('transaction', forceRender);
    return () => {
      overlayEditor.off('transaction', forceRender);
    };
  }, [overlayEditor]);

  // The Pages overlay positions itself from the header/footer band's rect at
  // open time, but opening also flips this hook's state, and the resulting
  // toolbar re-render shifts the editor for a frame — the overlay anchors to
  // that transient rect. floating-ui can't recover: the overlay's anchor is a
  // virtual element with no contextElement, so its autoUpdate only sees window
  // scroll/resize, never layout shifts. A synthetic window scroll event re-runs
  // the overlay's position function once layout has settled.
  React.useEffect(() => {
    if (!overlayEditor) return undefined;
    const reposition = (): void => {
      window.dispatchEvent(new Event('scroll'));
    };
    let raf2 = 0;
    const raf1 = window.requestAnimationFrame(() => {
      raf2 = window.requestAnimationFrame(reposition);
    });
    const timer = window.setTimeout(reposition, 300);
    return () => {
      window.cancelAnimationFrame(raf1);
      window.cancelAnimationFrame(raf2);
      window.clearTimeout(timer);
    };
  }, [overlayEditor]);

  return overlayEditor;
}
